'use client';

/**
 * Lightweight SWR-style cache for site data.
 *
 * - Returns cached data INSTANTLY if available (no loading flash on navigation)
 * - Revalidates in background for freshness
 * - Single in-flight request per key (deduped across components)
 * - NO "Loading..." blocking renders — components always render their current data
 */

import { useEffect, useState, useSyncExternalStore } from 'react';

type Listener = () => void;

interface Entry<T> {
  data: T | undefined;
  error: unknown;
  loading: boolean;
  timestamp: number;
  listeners: Set<Listener>;
  inflight: Promise<T> | null;
}

const cache = new Map<string, Entry<unknown>>();
// Keep the client-side cache window short so admin edits show up on the
// public site within a few seconds of save. The cache-version header still
// lets the server force a full purge when it wants to.
const FRESH_MS = 5_000;
const VERSION_STORAGE_KEY = 'cache_version_seen';
const VERSION_HEADER = 'x-cache-version';

/**
 * Wipe the entire in-memory cache. Exposed so the admin UI can force a
 * full client-side purge after hitting POST /api/admin/purge-cache.
 */
export function clearAll() {
  cache.forEach((entry) => {
    entry.data = undefined;
    entry.error = null;
    entry.timestamp = 0;
    entry.inflight = null;
    notify(entry);
  });
  cache.clear();
}

/**
 * Compare the `x-cache-version` response header against the last value we
 * saw (stored in sessionStorage). If the server has bumped it, wipe the
 * in-memory cache so subsequent reads refetch.
 */
function reconcileCacheVersion(res: Response) {
  if (typeof window === 'undefined') return;
  const version = res.headers.get(VERSION_HEADER);
  if (!version) return;
  try {
    const seen = window.sessionStorage.getItem(VERSION_STORAGE_KEY);
    if (seen !== version) {
      window.sessionStorage.setItem(VERSION_STORAGE_KEY, version);
      if (seen !== null) {
        // Only purge when the version actually *changed* (not on first-ever load).
        clearAll();
      }
    }
  } catch {
    /* sessionStorage unavailable (private mode, SSR) — ignore */
  }
}

function getEntry<T>(key: string): Entry<T> {
  let entry = cache.get(key) as Entry<T> | undefined;
  if (!entry) {
    entry = {
      data: undefined,
      error: null,
      loading: false,
      timestamp: 0,
      listeners: new Set(),
      inflight: null,
    };
    cache.set(key, entry);
  }
  return entry;
}

function notify<T>(entry: Entry<T>) {
  entry.listeners.forEach((l) => l());
}

async function fetchAndStore<T>(key: string, url: string): Promise<T> {
  const entry = getEntry<T>(key);
  if (entry.inflight) return entry.inflight;

  entry.loading = true;
  notify(entry);

  const promise = (async () => {
    try {
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
      reconcileCacheVersion(res);
      const json = (await res.json()) as T;
      entry.data = json;
      entry.error = null;
      entry.timestamp = Date.now();
      return json;
    } catch (err) {
      entry.error = err;
      throw err;
    } finally {
      entry.loading = false;
      entry.inflight = null;
      notify(entry);
    }
  })();

  entry.inflight = promise;
  return promise;
}

/**
 * Hook: useSiteData<T>(key, url, initialData)
 *
 * - Returns `initialData` instantly if cache is empty (NO loading flash)
 * - Shows cached data instantly on revisit
 * - Revalidates in background when cache is stale
 * - Always renders — there is no "loading" early-return pattern
 */
export function useSiteData<T>(key: string, url: string, initialData: T): T {
  const entry = getEntry<T>(key);
  const hasData = entry.data !== undefined;
  const initialSnapshot: T = hasData ? (entry.data as T) : initialData;

  const [value, setValue] = useState<T>(initialSnapshot);

  useEffect(() => {
    const listener = () => {
      const e = getEntry<T>(key);
      if (e.data !== undefined) setValue(e.data);
    };
    entry.listeners.add(listener);

    // If we have fresh cache, do nothing; else revalidate in background.
    const stale = !hasData || Date.now() - entry.timestamp > FRESH_MS;
    if (stale && !entry.inflight) {
      fetchAndStore<T>(key, url).catch(() => {
        /* swallow, keep showing defaults */
      });
    } else if (hasData) {
      // already have fresh data, make sure current state matches it
      setValue(entry.data as T);
    }

    return () => {
      entry.listeners.delete(listener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, url]);

  return value;
}

/**
 * Primitive that forces a refresh of a given cache key.
 */
export function invalidateSiteData(key: string) {
  const entry = cache.get(key);
  if (entry) {
    entry.timestamp = 0;
    entry.inflight = null;
  }
}
